import { isBoolean, isNull, isNumber, isString, isUndefined } from "lodash";
import { isVariation } from "../../helper";
import { VisitNodePassage } from "../../types";
import { numberFormatters } from "common";
import { dateFromNullable, getDateString } from "common/date-utils";
import { esgScores } from "common/esg-helpers";
import { NumberFormatKind } from "common/types";
import { benchmarkQuartileRankingWithUnknownValueFormatter } from "components/widgets/grid/renderers/benchmarkQuartileRenderer";
import { CommmonFormatEnum } from "datapoints";
import { DisplayValueType } from "store/current-dashboard/types";
/*
 * We have a lot of formatting enums and switch/cases in a lot of places
 * Putting them all in this file and dividing them by "rules" can be a first step to help reduce that number little by little.
 */
export const kpiSparklineFormatValue = (value, format, unit) => {
    switch (format) {
        case CommmonFormatEnum.percent:
            return `${numberFormatters(value, NumberFormatKind.percent, { minimumFractionDigits: 2 }).slice(0, -1)}%`;
        case CommmonFormatEnum.multiple:
            return `${numberFormatters(value, NumberFormatKind.compact, { minimumFractionDigits: 2 })}x`;
        case CommmonFormatEnum.value:
        case CommmonFormatEnum.number:
        case CommmonFormatEnum.year:
            return numberFormatters(value, NumberFormatKind.compact, { maximumFractionDigits: 1 });
        case CommmonFormatEnum.currency:
        case CommmonFormatEnum.amount:
            return numberFormatters(value, NumberFormatKind.currency, { currency: unit });
        case CommmonFormatEnum.area:
            return numberFormatters(value, NumberFormatKind.area, {
                maximumFractionDigits: 2,
                minimumFractionDigits: 2,
                unitOfArea: unit
            });
        case CommmonFormatEnum.unit:
            return `${numberFormatters(value, NumberFormatKind.unit, { minimumFractionDigits: 2, unitForValue: unit })}`;
        case CommmonFormatEnum.string:
        case CommmonFormatEnum.entity:
        case CommmonFormatEnum.boolean:
        case CommmonFormatEnum.date:
        case CommmonFormatEnum.esg:
        case CommmonFormatEnum.performancePeriod:
        case CommmonFormatEnum.quartile:
        default:
            return value?.toString() || "";
    }
};
const mapFormatValue = (item, format) => {
    switch (format) {
        case CommmonFormatEnum.currency:
            return numberFormatters(item.value, NumberFormatKind.currency, { currency: item.unit });
        case CommmonFormatEnum.area:
            return numberFormatters(item.value, NumberFormatKind.area, { maximumFractionDigits: 2, minimumFractionDigits: 2, unitOfArea: item.unit });
        case CommmonFormatEnum.unit:
            return numberFormatters(item.value, NumberFormatKind.unit, { maximumFractionDigits: 2, minimumFractionDigits: 2, unitForValue: item.unit });
        case CommmonFormatEnum.quartile:
            return benchmarkQuartileRankingWithUnknownValueFormatter({ value: item.value });
        default:
            return item.value.toString();
    }
};
const barFormatValue = (item, format, mode) => {
    if (mode === "short") {
        switch (format) {
            case CommmonFormatEnum.percent:
                return numberFormatters(item.value, NumberFormatKind.percent, { maximumFractionDigits: 1 });
            case CommmonFormatEnum.multiple:
                return numberFormatters(item.value, NumberFormatKind.multiple, { minimumFractionDigits: 2 });
            case CommmonFormatEnum.value:
                return numberFormatters(item.value, NumberFormatKind.compact);
            case CommmonFormatEnum.area:
            case CommmonFormatEnum.unit:
                return numberFormatters(item.value, NumberFormatKind.compact, { maximumFractionDigits: 1, minimumFractionDigits: 1, notation: "compact", unitOfArea: item.unit });
            default:
                return numberFormatters(item.value, NumberFormatKind.compact, { maximumFractionDigits: 1 });
        }
    }
    else if (mode === "full") {
        const options = { currency: item.unit, minimumFractionDigits: 2 };
        switch (format) {
            case CommmonFormatEnum.multiple:
                return `${numberFormatters(item.value, NumberFormatKind.compact, options)}x`;
            case CommmonFormatEnum.percent:
                return numberFormatters(item.value, NumberFormatKind.percent, options);
            case CommmonFormatEnum.amount:
                return numberFormatters(item.value, NumberFormatKind.currency, options);
            case CommmonFormatEnum.esg:
                return esgScores[esgScores.length - item.value - 1];
            case CommmonFormatEnum.value:
            case CommmonFormatEnum.year:
            case CommmonFormatEnum.number:
                return numberFormatters(item.value, NumberFormatKind.compact);
            case CommmonFormatEnum.area:
                return numberFormatters(item.value, NumberFormatKind.area, { maximumFractionDigits: 2, minimumFractionDigits: 2, unitOfArea: item.unit });
            case CommmonFormatEnum.unit:
                return numberFormatters(item.value, NumberFormatKind.unit, { maximumFractionDigits: 2, minimumFractionDigits: 2, unitForValue: item.unit });
            default:
                return item.value.toString();
        }
    }
    else if (mode === "percent") {
        return numberFormatters(item.value, NumberFormatKind.percent, { minimumFractionDigits: 2 });
    }
    else if (mode === "temp-risk-measures-format-until-they-are-added-to-the-designer") {
        return numberFormatters(item.value, NumberFormatKind.compact, { maximumFractionDigits: 2 });
    }
    return numberFormatters(item.value, NumberFormatKind.compact, { maximumFractionDigits: 1 });
};
const getValue = (value) => {
    if (isBoolean(value) || isString(value) || isNull(value) || isUndefined(value)) {
        return { value: 0 };
    }
    if (isNumber(value)) {
        return { value };
    }
    if ("amount" in value) {
        return { unit: value.currency, value: value.amount || 0 };
    }
    if ("area" in value) {
        return { unit: value.unit, value: value.area || 0 };
    }
    if ("value" in value) {
        return { unit: value.unit, value: value.value || 0 };
    }
    return { value: 0 };
};
const getDatapointFormat = (args, datapointName) => {
    const request = args.context.request?.requests?.[0];
    const datapointId = (request?.measures || []).concat(request?.dimensions || []).find((d) => d.name === datapointName)?.datapoint;
    const datapointMetadata = args.context.datapointsMetadata?.find((d) => d.id === datapointId?.standard || d.id === datapointId?.custom)?.metadata;
    const datapointFormat = datapointMetadata?.format;
    return datapointFormat;
};
const createKpiFormattedMeasure = (value, format) => {
    if (isVariation(value)) {
        const variation = value;
        const { value: current, unit } = getValue(variation.currentValue), { value: previous } = getValue(variation.previousValue);
        return {
            change: variation.change,
            changePercent: variation.changePercent,
            currentValue: kpiSparklineFormatValue(current, format, unit),
            previousValue: kpiSparklineFormatValue(previous, format, unit)
        };
    }
    else if (format === CommmonFormatEnum.date) {
        return getDateString(dateFromNullable(value?.toString()));
    }
    else if (isString(value)) {
        return value;
    }
    const { value: raw, unit } = getValue(value);
    return kpiSparklineFormatValue(raw, format, unit);
};
const getBarFormat = (format, displayValueType) => (displayValueType === DisplayValueType.Percent ? CommmonFormatEnum.percent : format);
const createBarFormattedMeasure = (value, format, mode, displayValueType) => {
    let targetFormat = getBarFormat(format, displayValueType);
    if (isVariation(value)) {
        const variation = value;
        const change = barFormatValue(getValue(displayValueType === DisplayValueType.Percent ? variation.changePercent : variation.change), targetFormat, mode);
        if (mode === "short") {
            return change;
        }
        if (targetFormat === CommmonFormatEnum.percent && format === CommmonFormatEnum.amount) {
            targetFormat = CommmonFormatEnum.amount;
        }
        return {
            change,
            currentValue: barFormatValue(getValue(variation.currentValue), targetFormat, mode),
            previousValue: barFormatValue(getValue(variation.previousValue), targetFormat, mode)
        };
    }
    if (targetFormat === CommmonFormatEnum.percent && format === CommmonFormatEnum.amount) {
        return barFormatValue(getValue(value), CommmonFormatEnum.amount, mode);
    }
    return barFormatValue(getValue(value), targetFormat, mode);
};
export const addFormattedValueVisitor = (field, source, format, rule, fallbackFormat) => (args) => {
    if (args?.context?.visitNodePassage !== VisitNodePassage.Leaf) {
        return;
    }
    if (args.visitable.measures && source in args.visitable.measures) {
        const datapointFormat = getDatapointFormat(args, source) ?? fallbackFormat;
        switch (rule) {
            case "kpi":
                args.visitable.measures[field] = createKpiFormattedMeasure(args.visitable.measures[source], format);
                break;
            case "map":
                args.visitable.measures[field] = mapFormatValue(getValue(args.visitable.measures[source]), format);
                break;
            case "bar":
            case "surface":
            case "donut":
                args.visitable.measures[field] = createBarFormattedMeasure(args.visitable.measures[source], datapointFormat, format, args.context.widgetState?.displayValueType);
                if (!args.visitable.additionalData) {
                    args.visitable.additionalData = {};
                }
                args.visitable.additionalData.format = getBarFormat(datapointFormat, args.context.widgetState?.displayValueType);
                break;
            default:
                break;
        }
    }
};
